import React, { useState } from "react";
import "../styles/ViolationItem.css";
import type { Result, CheckResult, NodeResult } from "axe-core";
import { IMPACT_CONFIG, type Impact } from "../impactConfig";

// Per-rule authoring guidance (keyed by axe rule id) shown when a finding is
// expanded — for rules whose fix needs an author decision axe can't infer.
const RULE_HINTS: Record<string, string> = {
  "video-caption":
    'If the video has spoken dialogue, add a caption track (<track kind="captions">). ' +
    "If it has only music or sound effects, the captions should note those sounds (e.g. [upbeat music]). " +
    "If the video is completely silent, captions don't apply — instead provide a text alternative describing the visuals (WCAG 1.2.1), such as a nearby transcript or summary.",
  "no-autoplay-audio":
    "Avoid autoplaying audio/video for more than 3 seconds, or provide a visible control to pause or mute it (WCAG 1.4.2). Adding the muted attribute on autoplay also satisfies this."
};

// Converts axe tags like "wcag143" -> "WCAG 1.4.3"; skips level-only tags ("wcag2a", "wcag21aa").
function wcagCriteria(tags: string[]): string[] {
  return tags.flatMap((t) => {
    const match = t.match(/^wcag(\d{3,})$/);
    return match ? ["WCAG " + match[1].split("").join(".")] : [];
  });
}

// Extracts human-readable fix messages from axe's any/all/none check buckets.
function checkMessages(checks: CheckResult[]): string[] {
  return checks
    .map((c) => c.message)
    .filter((m): m is string => typeof m === "string" && m.trim().length > 0);
}

// Flattens axe target arrays (including shadow DOM / iframe nested arrays) to a selector string.
function formatTarget(target: (string | string[])[]): string {
  return target
    .map((part) => (Array.isArray(part) ? part.join(" > ") : part))
    .join(" > ");
}

export const ViolationItem: React.FC<{
  violation: Result;
  onNodeClick?: (target: (string | string[])[], node?: NodeResult) => void;
}> = ({ violation, onNodeClick }) => {
  const [expanded, setExpanded] = useState(false);
  const cfg =
    IMPACT_CONFIG[(violation.impact ?? "minor") as Impact] ??
    IMPACT_CONFIG.minor;
  const criteria = wcagCriteria(violation.tags);

  return (
    <li className="a11y-violation-item">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        className="a11y-violation-trigger"
      >
        <div className="a11y-violation-badge-row">
          <span
            className="a11y-violation-badge"
            style={{ background: cfg.bg, color: cfg.color }}
          >
            {cfg.label}
          </span>
          <span className="a11y-violation-id">{violation.id}</span>
          <span className="a11y-violation-count">
            {violation.nodes.length} element
            {violation.nodes.length !== 1 ? "s" : ""} {expanded ? "▲" : "▼"}
          </span>
        </div>

        <span className="a11y-violation-description">
          {violation.description}
        </span>

        {criteria.length > 0 && (
          <div className="a11y-wcag-tags">
            {criteria.map((c) => (
              <span key={c} className="a11y-wcag-tag">
                {c}
              </span>
            ))}
          </div>
        )}
      </button>

      {violation.helpUrl && (
        <a
          href={violation.helpUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="a11y-learn-more"
        >
          Learn more ↗
        </a>
      )}

      {expanded && (
        <>
          {RULE_HINTS[violation.id] && (
            <p className="a11y-authoring-hint">{RULE_HINTS[violation.id]}</p>
          )}
          <ul className="a11y-node-list">
            {violation.nodes.map((node, i) => {
              const fixMessages = [
                ...checkMessages(node.any),
                ...checkMessages(node.all),
                ...checkMessages(node.none)
              ];

              return (
                <li
                  key={i}
                  className="a11y-node-item"
                  style={{ borderLeft: `3px solid ${cfg.color}` }}
                >
                  <div className="a11y-node-item-header">
                    <code className="a11y-node-selector">
                      {formatTarget(node.target as (string | string[])[])}
                    </code>
                    {onNodeClick && (
                      <button
                        type="button"
                        className="a11y-node-jump-btn"
                        title="Highlight this element in the editor"
                        aria-label="Highlight this element in the editor"
                        onClick={(e) => {
                          e.stopPropagation();
                          onNodeClick(
                            node.target as (string | string[])[],
                            node
                          );
                        }}
                      >
                        ⊙
                      </button>
                    )}
                  </div>

                  {fixMessages.length > 0 ? (
                    <ul className="a11y-fix-messages">
                      {fixMessages.map((msg, j) => (
                        <li key={j} className="a11y-fix-message">
                          {msg}
                        </li>
                      ))}
                    </ul>
                  ) : node.failureSummary ? (
                    <p className="a11y-fix-summary">
                      {node.failureSummary
                        .replace(/^Fix (?:any|all) of the following:\s*/i, "")
                        .trim()}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </li>
  );
};
